import { FormEvent, useEffect, useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, Bell, CheckCircle2, Globe2, KeyRound, MonitorCog, Palette, Save, Volume2 } from 'lucide-react';
import { fetchApi } from '../api';
import {
  DevicePrefs,
  SettingsLanguage,
  SettingsTheme,
  UserSettings,
  getDevicePrefs,
  saveUserSettings,
  setDevicePrefs,
  useUserSettings,
} from '../utils/userSettings';
import '../styles/settings.css';

/**
 * Preferences screen.
 *
 * Previously: every field was written to `localStorage`, the server never heard
 * about any of it, and the "Настройки сохранены" toast appeared unconditionally —
 * including when nothing had been saved anywhere. The notification switches
 * changed no behaviour at all, and the timezone selector had no effect on a single
 * number in the product.
 *
 * Now: the account's preferences are loaded from and written to
 * `GET`/`PATCH /api/settings`. The toast appears only after the request succeeds,
 * a failure shows the server's own message, and the notification switches are
 * enforced server-side in `NotificationService` — a muted category is not
 * created and its email is not enqueued.
 *
 * The two preferences that really are per-device say so on the row itself.
 */

type NotificationKey = 'notifyLeads' | 'notifyDeals' | 'notifyStock' | 'notifyBilling' | 'notifyPush' | 'notifyEmail';

const THEME_OPTIONS: { value: SettingsTheme; label: string }[] = [
  { value: 'light', label: 'Светлая' },
  { value: 'dark', label: 'Тёмная' },
  { value: 'system', label: 'Как в системе' },
];

const NOTIFICATION_ROWS: { key: NotificationKey; title: string; description: string }[] = [
  { key: 'notifyLeads', title: 'Новые обращения', description: 'Первое сообщение от клиента в любом подключённом канале' },
  { key: 'notifyDeals', title: 'Сделки и заявки', description: 'Создание, изменение суммы и закрытие сделок' },
  { key: 'notifyStock', title: 'Остатки на складе', description: 'Товар заканчивается — не больше 5 штук' },
  { key: 'notifyBilling', title: 'Тариф и платежи', description: 'Оплата, продление и окончание оплаченного периода' },
];

const DELIVERY_ROWS: { key: NotificationKey; title: string; description: string }[] = [
  {
    key: 'notifyPush',
    title: 'Показывать сразу в браузере',
    description: 'Всплывающее уведомление в момент события. Без него уведомления просто ждут в списке колокольчика',
  },
  { key: 'notifyEmail', title: 'Дублировать на email', description: 'Те же события письмом на адрес аккаунта' },
];

const settingsFields: (keyof UserSettings)[] = [
  'language',
  'theme',
  'notifyEmail',
  'notifyPush',
  'notifyLeads',
  'notifyDeals',
  'notifyStock',
  'notifyBilling',
];

export default function SettingsPage() {
  const { settings, status, error: loadError, reload } = useUserSettings();
  const [draft, setDraft] = useState<UserSettings>(settings);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [device, setDevice] = useState<DevicePrefs>(getDevicePrefs);

  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  // Adopt whatever the server says, unless the user is mid-edit.
  useEffect(() => {
    setDraft((current) => (dirtyAgainst(current, settings) && status === 'ready' ? current : settings));
  }, [settings, status]);

  const dirty = useMemo(() => dirtyAgainst(draft, settings), [draft, settings]);
  const locked = status !== 'ready';

  const update = <K extends keyof UserSettings>(key: K, value: UserSettings[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setSaved(false);
    setSaveError(null);
  };

  const updateDevice = (patch: Partial<DevicePrefs>) => setDevice(setDevicePrefs(patch));

  const savePreferences = async () => {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const patch: Partial<UserSettings> = {};
      for (const field of settingsFields) {
        if (draft[field] !== settings[field]) patch[field] = draft[field] as never;
      }
      if (Object.keys(patch).length === 0) return;
      await saveUserSettings(patch);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 3000);
    } catch (err: unknown) {
      // The real reason, not a green toast.
      setSaveError(err instanceof Error ? err.message : 'Не удалось сохранить настройки');
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (event: FormEvent) => {
    event.preventDefault();
    setPasswordError('');
    setPasswordMessage('');
    if (passwords.newPassword !== passwords.confirmPassword) {
      setPasswordError('Новые пароли не совпадают');
      return;
    }
    setPasswordSaving(true);
    try {
      await fetchApi('/auth/password', {
        method: 'PATCH',
        body: JSON.stringify({ currentPassword: passwords.currentPassword, newPassword: passwords.newPassword }),
      });
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordMessage('Пароль успешно изменён');
    } catch (err: any) {
      setPasswordError(err.message || 'Не удалось изменить пароль');
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <section className="view active account-page settings-page">
      <div className="account-heading settings-heading">
        <div>
          <span className="eyebrow">Рабочее пространство</span>
          <h1>Настройки</h1>
          <p>Уведомления, оформление и безопасность аккаунта.</p>
        </div>
        <button className="btn btn-primary" onClick={savePreferences} disabled={locked || saving || !dirty}>
          <Save size={16} />
          {saving ? 'Сохраняем…' : dirty ? 'Сохранить настройки' : 'Всё сохранено'}
        </button>
      </div>

      {status === 'loading' && (
        <div className="settings-status" role="status">
          Загружаем настройки аккаунта…
        </div>
      )}

      {status === 'error' && (
        <div className="settings-status error" role="alert">
          <AlertTriangle size={16} />
          {loadError || 'Не удалось загрузить настройки'}
          <button type="button" className="settings-status-retry" onClick={reload}>
            Повторить
          </button>
        </div>
      )}

      {saveError && (
        <div className="settings-status error" role="alert">
          <AlertTriangle size={16} />
          {saveError}
        </div>
      )}

      {saved && (
        <div className="settings-toast" role="status">
          <CheckCircle2 size={18} />
          Настройки сохранены
        </div>
      )}

      <div className="settings-layout">
        <div className="settings-main">
          {status === 'loading' ? (
            <div className="settings-skeleton" aria-hidden="true">
              <i />
              <i />
              <i />
            </div>
          ) : (
            <>
              <article className="account-card settings-card">
                <header>
                  <span className="section-icon">
                    <Bell size={19} />
                  </span>
                  <div>
                    <h2>О чём уведомлять</h2>
                    <p>Выключенная категория не создаёт уведомление и не отправляет письмо.</p>
                  </div>
                </header>
                <div className="setting-list">
                  {NOTIFICATION_ROWS.map((row) => (
                    <SettingToggle
                      key={row.key}
                      title={row.title}
                      description={row.description}
                      checked={draft[row.key]}
                      disabled={locked || saving}
                      onChange={(value) => update(row.key, value)}
                    />
                  ))}
                </div>
              </article>

              <article className="account-card settings-card">
                <header>
                  <span className="section-icon neutral">
                    <MonitorCog size={19} />
                  </span>
                  <div>
                    <h2>Как доставлять</h2>
                    <p>Способ доставки для всех включённых выше категорий.</p>
                  </div>
                </header>
                <div className="setting-list">
                  {DELIVERY_ROWS.map((row) => (
                    <SettingToggle
                      key={row.key}
                      title={row.title}
                      description={row.description}
                      checked={draft[row.key]}
                      disabled={locked || saving}
                      onChange={(value) => update(row.key, value)}
                    />
                  ))}
                  <SettingToggle
                    icon={<Volume2 size={18} />}
                    title="Звук уведомлений"
                    description="Короткий сигнал при новом уведомлении"
                    scopeNote="только в этом браузере"
                    checked={device.sound}
                    onChange={(value) => updateDevice({ sound: value })}
                  />
                </div>
              </article>

              <article className="account-card settings-card">
                <header>
                  <span className="section-icon neutral">
                    <Globe2 size={19} />
                  </span>
                  <div>
                    <h2>Язык и оформление</h2>
                    <p>Применяется ко всему рабочему пространству сразу после сохранения.</p>
                  </div>
                </header>
                <div className="form-grid">
                  <label className="account-field">
                    <span>Язык и формат данных</span>
                    <select
                      value={draft.language}
                      disabled={locked || saving}
                      onChange={(event) => update('language', event.target.value as SettingsLanguage)}
                    >
                      <option value="ru">Русский</option>
                      <option value="en">English</option>
                    </select>
                    <small>
                      Влияет на формат дат, времени и чисел во всём интерфейсе. Текст интерфейса пока только на русском —
                      перевода нет, и мы не показываем его как доступный.
                    </small>
                  </label>
                  <div className="account-field">
                    <span>Тема оформления</span>
                    <div className="theme-choice" role="group" aria-label="Тема оформления">
                      {THEME_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          aria-pressed={draft.theme === option.value}
                          disabled={locked || saving}
                          onClick={() => update('theme', option.value)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <small>«Как в системе» следует настройке операционной системы.</small>
                  </div>
                </div>
              </article>
            </>
          )}

          <form className="account-card settings-card" onSubmit={changePassword}>
            <header>
              <span className="section-icon danger-soft">
                <KeyRound size={19} />
              </span>
              <div>
                <h2>Смена пароля</h2>
                <p>Новый пароль должен содержать не менее 12 символов.</p>
              </div>
            </header>
            {(passwordMessage || passwordError) && (
              <div className={`form-message ${passwordError ? 'error' : ''}`} role={passwordError ? 'alert' : 'status'}>
                {passwordError || passwordMessage}
              </div>
            )}
            <div className="form-grid password-grid">
              <label className="account-field">
                <span>Текущий пароль</span>
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={passwords.currentPassword}
                  onChange={(event) => setPasswords((current) => ({ ...current, currentPassword: event.target.value }))}
                />
              </label>
              <label className="account-field">
                <span>Новый пароль</span>
                <input
                  type="password"
                  required
                  minLength={12}
                  autoComplete="new-password"
                  value={passwords.newPassword}
                  onChange={(event) => setPasswords((current) => ({ ...current, newPassword: event.target.value }))}
                />
              </label>
              <label className="account-field">
                <span>Повторите пароль</span>
                <input
                  type="password"
                  required
                  minLength={12}
                  autoComplete="new-password"
                  value={passwords.confirmPassword}
                  onChange={(event) => setPasswords((current) => ({ ...current, confirmPassword: event.target.value }))}
                />
              </label>
            </div>
            <footer>
              <button className="btn btn-outline" disabled={passwordSaving}>
                {passwordSaving ? 'Обновляем…' : 'Обновить пароль'}
              </button>
            </footer>
          </form>
        </div>

        <aside className="settings-side account-card">
          <header>
            <span className="section-icon neutral">
              <Palette size={19} />
            </span>
            <div>
              <h2>Плотность интерфейса</h2>
              <p>Сколько данных помещается на экране.</p>
            </div>
          </header>
          <SettingToggle
            title="Компактный режим"
            description="Меньше отступов в таблицах и списках"
            scopeNote="только в этом браузере"
            checked={device.compactMode}
            onChange={(value) => updateDevice({ compactMode: value })}
          />
          <div className={`density-preview ${device.compactMode ? 'compact' : ''}`} aria-hidden="true">
            <i />
            <i />
            <i />
          </div>
          <p className="settings-hint settings-hint-block">
            Компактный режим и звук хранятся в этом браузере: для них нет поля в аккаунте, поэтому на другом устройстве
            их придётся включить заново. Всё остальное на этой странице привязано к аккаунту.
          </p>
        </aside>
      </div>
    </section>
  );
}

function dirtyAgainst(draft: UserSettings, settings: UserSettings): boolean {
  return settingsFields.some((field) => draft[field] !== settings[field]);
}

function SettingToggle({
  icon,
  title,
  description,
  scopeNote,
  checked,
  disabled,
  onChange,
}: {
  icon?: ReactNode;
  title: string;
  description: string;
  scopeNote?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className={`setting-row ${disabled ? 'is-busy' : ''}`}>
      {icon && <span className="setting-row-icon">{icon}</span>}
      <span className="setting-copy">
        <strong>{title}</strong>
        <small>{description}</small>
        {scopeNote && <span className="settings-scope-note">{scopeNote}</span>}
      </span>
      <span className="modern-switch">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
        <i />
      </span>
    </label>
  );
}
